import { Component, ChangeDetectionStrategy, ElementRef, HostListener, Input, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';

import { LanguageService } from '@core/i18n/language.service';
import { AuthService } from '@core/auth/auth.service';
import { ApiSysService } from '@core/api/api-sys.service';
import { alpha3ToAlpha2 } from '@shared/utils/string-utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface Language {
  lang_name: string;
}

@Component({
    selector: 'app-language-selector',
    imports: [],
    templateUrl: './language-selector.component.html',
    styleUrls: ['./language-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LanguageSelectorComponent implements OnInit, OnDestroy {
  private readonly langService = inject(LanguageService);
  private readonly authService = inject(AuthService);
  private readonly apiSysService = inject(ApiSysService);
  private readonly elementRef = inject(ElementRef);

  readonly langs = signal<Language[]>([]);
  readonly selectedLanguage = signal<string | null>(null);
  readonly selectedLanguageFlag = computed(() => {
    const lang = this.selectedLanguage();
    return lang ? this.convertToAlpha2Code(lang) : null;
  });
  public dropdownOpen = false;

  @Input() loginValue: boolean | null = false;

  private readonly destroy$ = new Subject<void>();

  private readonly languageNames: Record<string, string> = {
    'eng': 'English',
    'spa': 'Español',
    'cat': 'Català',
    'fra': 'Français',
    'ita': 'Italiano',
    'deu': 'Deutsch',
    'por': 'Português',
    'rus': 'Русский',
    'ara': 'العربية'
  };

  public ngOnInit(): void {
    this.selectedLanguage.set(this.langService.getLanguage());

    this.langService.getSupportedLanguages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.langs.set(data as Language[]);
        },
        error: (error) => {
          console.error('Error fetching supported languages:', error);
        }
      });

    this.langService.forceChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (newLang: string | null) => {
          if (newLang) {
            this.selectedLanguage.set(newLang);
          }
        },
        error: (error) => {
          console.error('Error in language change subscription:', error);
        }
      });
  }

  public convertToAlpha2Code(alpha3: string): string {
    const flagCode = alpha3ToAlpha2(alpha3);
    return flagCode || 'eng';
  }

  public getLanguageCode(langName: string | null): string {
    return langName || 'eng';
  }

  public getLanguageDisplayName(langName: string): string {
    const code = this.getLanguageCode(langName);
    return this.languageNames[code] || langName;
  }

  public trackByLang(index: number, lang: Language): string {
    return lang.lang_name;
  }

  public toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.dropdownOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.dropdownOpen = false;
    }
  }

  private applyLanguage(langName: string): void {
    this.selectedLanguage.set(langName);
    this.langService.setLanguage(langName);
    this.dropdownOpen = false;
    this.persistLanguageToBackend(langName);
  }

  private persistLanguageToBackend(langName: string): void {
    const langId = this.langService.getLangIdByCode(langName);
    const personnelId = Number(this.authService.getUserId());
    const token = this.authService.getToken();
    if (langId && personnelId) {
      this.apiSysService.patchPersonnel(token, personnelId, { lang_id: langId }).subscribe();
    }
  }

  public selectLanguage(lang: Language): void {
    if (this.selectedLanguage() === lang.lang_name) {
      return;
    }
    this.applyLanguage(lang.lang_name);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
